import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { AdminStats } from './AdminStats';
import { Loader2, RefreshCw } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    averageEnergy: 0,
    alertsToday: 0,
    energyData: [],
    userTypes: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get total users count
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (usersError) throw usersError;

      // Get active users (users who made assessments in the last 24h)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      const { data: activeData, error: activeError } = await supabase
        .from('assessments')
        .select('user_id')
        .gt('created_at', yesterday.toISOString())
        .limit(1);

      if (activeError) throw activeError;

      // Get average energy level
      const { data: energyData, error: energyError } = await supabase
        .from('assessments')
        .select('overall_energy')
        .gt('created_at', yesterday.toISOString());

      if (energyError) throw energyError;

      const averageEnergy = energyData?.length 
        ? Math.round(energyData.reduce((acc, curr) => acc + curr.overall_energy, 0) / energyData.length)
        : 0;

      // Get alerts count
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: alertsData, error: alertsError } = await supabase
        .from('assessments')
        .select('*')
        .eq('zone', 'red')
        .gt('created_at', today.toISOString());

      if (alertsError) throw alertsError;

      // Get energy data for chart
      const { data: chartData, error: chartError } = await supabase
        .from('assessments')
        .select('created_at, overall_energy')
        .gt('created_at', yesterday.toISOString())
        .order('created_at', { ascending: true });

      if (chartError) throw chartError;

      const energyChartData = chartData?.map(d => ({
        time: new Date(d.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        value: d.overall_energy
      })) || [];

      // Get user types distribution
      const { data: typesData, error: typesError } = await supabase
        .from('profiles')
        .select('energy_type');

      if (typesError) throw typesError;

      const typeCount = typesData?.reduce((acc: Record<string, number>, curr) => {
        acc[curr.energy_type] = (acc[curr.energy_type] || 0) + 1;
        return acc;
      }, {});

      const userTypes = Object.entries(typeCount || {}).map(([name, value]) => ({
        name,
        value
      }));

      setStats({
        totalUsers: usersData?.length || 0,
        activeUsers: activeData?.length || 0,
        averageEnergy,
        alertsToday: alertsData?.length || 0,
        energyData: energyChartData,
        userTypes
      });

    } catch (error: any) {
      console.error('Error loading stats:', error);
      setError(error.message || 'Une erreur est survenue lors du chargement des statistiques');
      toast({
        variant: "destructive",
        title: "Erreur de chargement",
        description: "Impossible de charger les statistiques. Veuillez réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // Update stats every 5 minutes
    const interval = setInterval(loadStats, 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <Button 
          onClick={loadStats}
          variant="outline" 
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <AdminStats
        totalUsers={stats.totalUsers}
        activeUsers={stats.activeUsers}
        averageEnergy={stats.averageEnergy}
        alertsToday={stats.alertsToday}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-all">
          <CardHeader>
            <h3 className="text-lg font-semibold">Évolution de l'énergie</h3>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.energyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1E40AF"
                    strokeWidth={2}
                    dot={{ fill: '#1E40AF', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all">
          <CardHeader>
            <h3 className="text-lg font-semibold">Distribution des types</h3>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.userTypes}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.userTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}